import { equals, lerp } from "../utils";
import type {
    EndAnimationListener,
    TimeProvider,
    TimingFunction,
    UpdateAnimationListener,
} from "./interfaces";
import { timingFunctions } from "./timing";

class Animation {
    public value: number;
    public fromValue: number;
    public toValue: number;
    public duration: number;
    public timingFunction: TimingFunction;
    public onEnd?: EndAnimationListener;
    public updateListeners: UpdateAnimationListener[];
    public delay: number;

    private startTime: number;
    private timeProvider: TimeProvider;

    constructor(timeProvider: TimeProvider, value: number, duration: number) {
        this.value = value;
        this.fromValue = value;
        this.toValue = value;
        this.duration = duration;
        this.timingFunction = timingFunctions.ease;
        this.timeProvider = timeProvider;
        this.startTime = this.timeProvider.time;
        this.updateListeners = [];
        this.delay = 0;
    }

    public onUpdate(listener: UpdateAnimationListener) {
        this.updateListeners.push(listener);

        return this;
    }

    public offUpdate(listener: UpdateAnimationListener) {
        const index = this.updateListeners.indexOf(listener);

        if (index !== -1) {
            this.updateListeners.splice(index, 1);
        }

        return this;
    }

    public get progress() {
        const elapsed =
            (this.timeProvider.time - this.startTime - this.delay) /
            this.duration;

        if (elapsed < 0) {
            return 0;
        } else if (elapsed > 1) {
            return 1;
        }

        return elapsed;
    }

    public get isFinished() {
        return equals(this.value, this.toValue);
    }

    public update() {
        if (this.timeProvider.time - this.startTime < this.delay) {
            return false;
        }

        if (this.isFinished) {
            return false;
        }

        const progress = this.timingFunction(this.progress);
        const newValue = lerp(this.fromValue, this.toValue, progress);

        this.value = newValue;

        return true;
    }

    public play(toValue: number): this {
        this.startTime = this.timeProvider.time;
        this.fromValue = this.value;
        this.toValue = toValue;

        return this;
    }

    public stop(): this {
        this.fromValue = this.value;
        this.toValue = this.value;
        this.startTime = this.timeProvider.time;

        return this;
    }

    public set(newValue: number): this {
        this.fromValue = newValue;
        this.toValue = newValue;
        this.value = newValue;

        return this;
    }
}

export default Animation;
